import { isPrinterSupported, printRaster } from "../../platform/printer";
import {
  DEFAULT_WIDTH_DOTS,
  resolvePrinterFor,
  type ResolvedPrinter,
} from "../../platform/printerRegistry";
import { tuningToJobOptions } from "../../platform/printSettings";
import { toCatPrinterJob } from "../../receipt/catPrinter";
import { renderItemLabel, type RenderedLabel } from "../../receipt/labelRender";

/**
 * G-100 — printing an item's barcode sticker, in one place.
 *
 * Owner, 2026-09-22: *"I just want to remove second click of 'Print a barcode'
 * when barcode exists and printer is already paired."* Before this, the first
 * tap only opened a panel and the panel's own button did the work; the same
 * four steps now live here, so the card's button and the fallback panel run
 * ONE implementation rather than two copies that drift.
 *
 * THE PRINTER IS AN ARGUMENT, never resolved inside the print. The caller
 * resolves once and the render and the send both come off that same object —
 * `printerRegistry.ts` explains why there is no `printTo(role, bytes)`: the
 * packing TRUNCATES a bitmap wider than the head with no error at all, so the
 * profile the bitmap was drawn for must be the profile it is sent to.
 *
 * NO WORDS LIVE HERE. `printItemSticker` reports an OUTCOME and hands back the
 * cause; the screen decides what a person reads, through the dictionaries. A
 * sentence in this file would be an untranslated string in the kiosk, which
 * the i18n gate refuses — and rightly.
 *
 * NO USER GESTURE IS NEEDED. `printRaster` connects to a deviceId already in
 * the registry and never calls `requestDevice`, so a print may start from
 * anywhere. Only PAIRING needs a person choosing from a list, which is why
 * `PairPrinterPrompt` stays behind its own button in the fallback panel.
 */

export interface ItemSticker {
  itemName: string;
  /** The text under the bars — an EAN, or the shop's own `IT000042` (G-99). */
  barcode: string;
}

/**
 * G-99 — what a sticker for this item would say, or null when there is
 * nothing to print yet. ONE rule, for the item card (G-100) and the
 * receiving row (G-130) alike.
 *
 * THE SHOP'S OWN CODE WINS over the manufacturer's when an item carries both.
 * An EAN may name several items (G-61), so scanning one opens a picker; ours
 * names exactly this item. Printing ours takes nothing away — the EAN stays
 * on the product and still scans.
 */
export function printableBarcode(item: {
  issuedBarcode: { barcodeText: string } | null;
  barcode: string | null;
}): string | null {
  return item.issuedBarcode?.barcodeText ?? item.barcode ?? null;
}

export type ItemStickerPrint =
  | {
      outcome: "printed";
      /**
       * The roll is too narrow for bars a camera can read. Advisory, reported
       * AFTER the fact: it cannot fire on this shop's 384-dot paper, and a
       * gate here would put back the confirmation this change removed.
       */
      tooNarrow: boolean;
    }
  | { outcome: "could-not-draw"; cause: unknown }
  | { outcome: "could-not-print"; cause: unknown };

/**
 * The printer a sticker would go to, or null when there is none — no native
 * shell (any browser), or nothing paired with this tablet.
 *
 * Read PER TAP, not once at mount: it is a `localStorage` read, so a printer
 * paired on another screen mid-session is picked up by the next press, and a
 * forgotten one stops being offered.
 */
export function itemStickerPrinter(): ResolvedPrinter | null {
  return isPrinterSupported() ? resolvePrinterFor("notes") : null;
}

/**
 * Draw the sticker for THIS machine's paper. Throws what `renderItemLabel`
 * throws — text Code128 cannot carry, or a WebView that refuses a 2D context.
 */
export function renderItemSticker(
  sticker: ItemSticker,
  printer: ResolvedPrinter | null,
): RenderedLabel {
  return renderItemLabel(
    { itemName: sticker.itemName, barcodeText: sticker.barcode },
    printer?.printer.widthDots ?? DEFAULT_WIDTH_DOTS,
  );
}

/** Send an already-drawn sticker to the machine it was drawn for. */
export async function printRenderedSticker(
  printer: ResolvedPrinter,
  rendered: RenderedLabel,
): Promise<void> {
  await printRaster(
    printer.printer,
    toCatPrinterJob(rendered.bitmap, {
      ...tuningToJobOptions(printer.printer.tuning),
      rowBytes: Math.ceil(printer.printer.widthDots / 8),
    }),
  );
}

/**
 * One tap: draw, then print, to the printer the caller resolved.
 *
 * NEVER THROWS. Every way this can fail is a thing a florist can act on — the
 * printer is asleep, Bluetooth is off, the text will not encode — so each one
 * comes back as an outcome the screen can put into words, rather than as an
 * exception a caller might forget to catch while paper is involved.
 */
export async function printItemSticker(
  printer: ResolvedPrinter,
  sticker: ItemSticker,
): Promise<ItemStickerPrint> {
  let rendered: RenderedLabel;
  try {
    rendered = renderItemSticker(sticker, printer);
  } catch (cause: unknown) {
    return { outcome: "could-not-draw", cause };
  }
  try {
    await printRenderedSticker(printer, rendered);
  } catch (cause: unknown) {
    return { outcome: "could-not-print", cause };
  }
  return { outcome: "printed", tooNarrow: rendered.layout.barcode.tooNarrow };
}
